/**
 * Wallet provider discovery & selection.
 *
 * Modern browser wallets (MetaMask, Uniswap Wallet, Coinbase, Rabby, Brave, ...)
 * advertise themselves via EIP-6963 instead of — or in addition to — the legacy
 * `window.ethereum` global. When several wallets are installed, `window.ethereum`
 * becomes an unreliable conflicted proxy and "first to announce" wins, which is
 * never what the user wants.
 *
 * This module discovers every announced provider, lets the UI present a picker,
 * and remembers the user's explicit choice across reloads. It falls back to the
 * legacy global so the dapp still connects with older single-injection wallets.
 */

const SELECTED_KEY = 'selectedWalletId';

// { info: { uuid, name, icon, rdns }, provider }
const discovered = [];
const listeners = new Set();
let selectedProvider = null;

const notify = () => {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      /* listener errors must not break discovery */
    }
  });
};

const addProvider = (detail) => {
  if (!detail || !detail.provider || !detail.info || !detail.info.rdns) return;
  if (discovered.some((d) => d.info.rdns === detail.info.rdns)) return;
  discovered.push(detail);
  notify();
};

if (typeof window !== 'undefined') {
  window.addEventListener('eip6963:announceProvider', (event) => addProvider(event.detail));
  // Ask any EIP-6963 wallets already loaded to (re)announce themselves.
  window.dispatchEvent(new Event('eip6963:requestProvider'));
}

const legacyName = (p) => {
  if (!p) return 'Browser Wallet';
  if (p.isMetaMask && !p.isBraveWallet) return 'MetaMask';
  if (p.isBraveWallet) return 'Brave Wallet';
  if (p.isCoinbaseWallet || p.isCoinbaseBrowser) return 'Coinbase Wallet';
  if (p.isRabby) return 'Rabby';
  if (p.isTrust || p.isTrustWallet) return 'Trust Wallet';
  if (p.isUniswapWallet || p.isUniswap) return 'Uniswap Wallet';
  if (p.isFrame) return 'Frame';
  return 'Browser Wallet';
};

/** Subscribe to provider-list / selection changes. Returns unsubscribe. */
export function onProvidersChanged(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Bind wallet event handlers (accountsChanged / chainChanged) to the active
 * provider, re-binding automatically if the provider is injected late or the
 * user switches wallets. Returns an unsubscribe that detaches everything.
 *
 * This avoids a race where a component mounts (e.g. on a hard reload with a
 * saved session) before the wallet has injected, leaving listeners unattached.
 */
export function subscribeWalletEvents({ onAccountsChanged, onChainChanged } = {}) {
  let bound = null;

  const detach = () => {
    if (!bound) return;
    if (onAccountsChanged) bound.removeListener('accountsChanged', onAccountsChanged);
    if (onChainChanged) bound.removeListener('chainChanged', onChainChanged);
    bound = null;
  };

  const bind = () => {
    const provider = getInjectedProvider();
    if (!provider || provider === bound) return;
    detach();
    bound = provider;
    if (onAccountsChanged) provider.on('accountsChanged', onAccountsChanged);
    if (onChainChanged) provider.on('chainChanged', onChainChanged);
  };

  bind();
  const off = onProvidersChanged(bind);

  return () => {
    off();
    detach();
  };
}

const getSavedWalletId = () => {
  try {
    return localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
};

/**
 * Normalised, selectable wallet list for the UI.
 * Prefers EIP-6963 wallets; falls back to legacy `window.ethereum` (incl. the
 * multi-provider array some legacy setups expose).
 */
export function getWalletOptions() {
  if (discovered.length > 0) {
    return discovered.map((d) => ({
      id: d.info.rdns || d.info.uuid,
      name: d.info.name,
      icon: d.info.icon || null,
      provider: d.provider,
    }));
  }

  const options = [];
  if (typeof window !== 'undefined' && window.ethereum) {
    const eth = window.ethereum;
    const list = Array.isArray(eth.providers) && eth.providers.length > 0 ? eth.providers : [eth];
    list.forEach((p, i) => {
      options.push({ id: `legacy-${i}-${legacyName(p)}`, name: legacyName(p), icon: null, provider: p });
    });
  }
  return options;
}

/** Pin the wallet the user explicitly chose and remember it across reloads. */
export function selectProvider(provider, id) {
  selectedProvider = provider || null;
  try {
    if (id) localStorage.setItem(SELECTED_KEY, id);
  } catch {
    /* storage may be unavailable */
  }
  notify();
}

/**
 * Best available injected EIP-1193 provider, or null.
 * Order: explicit selection -> previously-saved wallet (if present) -> first
 * discovered EIP-6963 wallet -> legacy global.
 */
export function getInjectedProvider() {
  if (selectedProvider) return selectedProvider;

  const options = getWalletOptions();
  const savedId = getSavedWalletId();
  if (savedId) {
    const match = options.find((o) => o.id === savedId);
    if (match) {
      selectedProvider = match.provider;
      return match.provider;
    }
  }
  return options[0] ? options[0].provider : null;
}

/**
 * Resolve an injected provider, waiting briefly for async injection and EIP-6963
 * announcements. If the user previously chose a wallet, prefer waiting for that
 * specific wallet (short grace window) before falling back to any available one,
 * so a reload doesn't silently query the wrong wallet.
 */
export function waitForProvider(timeoutMs = 3000) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(null);
      return;
    }

    const savedId = getSavedWalletId();
    const pickSaved = () => {
      if (!savedId) return null;
      const match = getWalletOptions().find((o) => o.id === savedId);
      return match ? match.provider : null;
    };

    // Fast paths.
    const savedNow = pickSaved();
    if (savedNow) {
      selectedProvider = savedNow;
      resolve(savedNow);
      return;
    }
    if (!savedId) {
      const anyNow = getInjectedProvider();
      if (anyNow) {
        resolve(anyNow);
        return;
      }
    }

    let settled = false;
    const finish = (provider) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (provider && savedId && provider === pickSaved()) selectedProvider = provider;
      resolve(provider);
    };

    const onChange = () => {
      const saved = pickSaved();
      if (saved) finish(saved);
    };

    const off = onProvidersChanged(onChange);
    window.addEventListener('ethereum#initialized', onChange, { once: true });
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    const poll = setInterval(onChange, 150);
    // If the saved wallet doesn't appear quickly, fall back to any available.
    const grace = setTimeout(() => {
      const p = pickSaved() || getInjectedProvider();
      if (p) finish(p);
    }, 1200);
    const timer = setTimeout(() => finish(pickSaved() || getInjectedProvider()), timeoutMs);

    function cleanup() {
      off();
      clearInterval(poll);
      clearTimeout(grace);
      clearTimeout(timer);
      window.removeEventListener('ethereum#initialized', onChange);
    }
  });
}
